'use client';

import { c as _c } from "react/compiler-runtime";
import type { GetRecommendationsQuery } from '@data-access/lib/gql/cart/documents';
import { GetRecommendationsDocument } from '@data-access/lib/gql/cart/documents';
import type { NexperienceTimeslotFragment } from '@data-access/lib/gql/experience/documents';
import { useGraphQL } from '@data-access/lib/graphql-query';
import { AddedToCart, addedToCartItemsAtom, buildExperienceCartItem } from '@feat/feat-cart/added-to-cart';
import { useCartItem } from '@feat/feat-cart/item';
import { CalendarX } from '@phosphor-icons/react';
import { useSetAtom } from 'jotai';
import { useAppExperienceTimeslots } from '../_hooks/use-app-experience-timeslots';
import { useExperienceFilterContext } from './app-experience-filter-provider';
import { AppExperienceOffersList } from './app-experience-offers-list';
const EXPERIENCE_OFFERS_CONTAINER_LABELS: Record<string, string> = {
  noOffers: 'Dieses Erlebnis ist sehr beliebt.',
  noOffersForDate: 'Dieses Datum ist sehr beliebt.',
  noOffersForDateInfo: 'Leider haben wir weniger verfügbare Plätze als du benötigst. Versuche gern eine neue Anfrage an einem anderen Tag oder verändere die Anzahl an Personen.',
  noOffersInfo: 'Leider haben wir weniger verfügbare Plätze als du benötigst. Versuche gern eine neue Anfrage und verändere die Anzahl an Personen.'
};
const EXPERIENCE_OFFERS_CONTAINER_INFO_LABELS: Record<string, string> = {
  noOffersForDate: 'Leider haben wir weniger verfügbare Plätze als du benötigst. Versuche gern eine neue Anfrage an einem anderen Tag oder verändere die Anzahl an Personen.',
  noOffers: 'Leider haben wir weniger verfügbare Plätze als du benötigst. Versuche gern eine neue Anfrage und verändere die Anzahl an Personen.'
};
export function AppExperienceOffersContainer() {
  const $ = _c(18);
  const {
    filter,
    experience
  } = useExperienceFilterContext();
  const {
    dates,
    isLoading,
    isFetching
  } = useAppExperienceTimeslots();
  let t0;
  if ($[0] !== experience.publicId) {
    t0 = {
      input: {
        id: experience.publicId,
        count: 1,
        type: "experience"
      }
    };
    $[0] = experience.publicId;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      enabled: false,
      select
    };
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const {
    data: recommendation,
    refetch: fetchRecommendations
  } = useGraphQL(GetRecommendationsDocument, t0, t1);
  const setAddedToCartItemsAtom = useSetAtom(addedToCartItemsAtom);
  const {
    addExperience
  } = useCartItem();
  let t2;
  if ($[3] !== addExperience || $[4] !== experience || $[5] !== fetchRecommendations || $[6] !== filter || $[7] !== setAddedToCartItemsAtom) {
    t2 = timeslot => new Promise(resolve => {
      if (!filter.selectedOptions) {
        resolve();
        return;
      }
      addExperience({
        timeslotId: timeslot.id,
        experienceId: experience.id,
        selectedOptions: filter.selectedOptions
      }, {
        onSuccess: () => {
          setAddedToCartItemsAtom([buildExperienceCartItem(experience, {
            startDate: timeslot.startDate.local,
            startTimezone: timeslot.startDate.timezone
          })]);
          fetchRecommendations().catch(_temp);
          resolve();
        }
      });
    });
    $[3] = addExperience;
    $[4] = experience;
    $[5] = fetchRecommendations;
    $[6] = filter;
    $[7] = setAddedToCartItemsAtom;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  const addCartItem = t2;
  if (!isLoading && !isFetching && dates.length === 0) {
    const t3 = filter.from || filter.to ? "noOffersForDate" : "noOffers";
    let t4;
    if ($[9] !== t3) {
      t4 = <EmptyList type={t3} />;
      $[9] = t3;
      $[10] = t4;
    } else {
      t4 = $[10];
    }
    return t4;
  }
  let t3;
  if ($[11] !== recommendation) {
    t3 = <AddedToCart recommendation={recommendation} />;
    $[11] = recommendation;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  let t4;
  if ($[13] !== addCartItem) {
    t4 = <AppExperienceOffersList onSubmit={addCartItem} />;
    $[13] = addCartItem;
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  let t5;
  if ($[15] !== t3 || $[16] !== t4) {
    t5 = <div className="pt-4 sm:pt-0">{t3}{t4}</div>;
    $[15] = t3;
    $[16] = t4;
    $[17] = t5;
  } else {
    t5 = $[17];
  }
  return t5;
}
function _temp() {}
function EmptyList(t0) {
  const $ = _c(8);
  const {
    type
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <CalendarX size={32} />;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const t2 = EXPERIENCE_OFFERS_CONTAINER_LABELS[type] ?? type;
  let t3;
  if ($[1] !== t2) {
    t3 = <h3 className="text-center font-heading3">{t2}</h3>;
    $[1] = t2;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const t4 = EXPERIENCE_OFFERS_CONTAINER_INFO_LABELS[type] ?? type;
  let t5;
  if ($[3] !== t4) {
    t5 = <p className="text-center font-body-copy">{t4}</p>;
    $[3] = t4;
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  let t6;
  if ($[5] !== t3 || $[6] !== t5) {
    t6 = <div className="fluid-container mt-4 flex flex-col items-center gap-y-6 rounded-xl bg-sand p-8 sm:mt-0">{t1}{t3}{t5}</div>;
    $[5] = t3;
    $[6] = t5;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  return t6;
}
function select({
  getRecommendations
}: GetRecommendationsQuery) {
  return getRecommendations;
}